import React, { useEffect, useMemo, useState } from "react";
import Sidebar from "../components/Sidebar";
import Card from "../components/Card";
import { goldAPI, mlmAPI, userAPI, walletAPI } from "../services/api";
import { tradeHistoryAPI } from "../services/tradeHistoryAPI";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

const DAY_MS = 24 * 60 * 60 * 1000;

const formatCurrency = (value) => {
  const numericValue = Number(value) || 0;
  return `$${numericValue.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const formatCount = (value) => {
  const numericValue = Number(value) || 0;
  return numericValue.toLocaleString();
};

const buildBuckets = (filter) => {
  const now = new Date();
  const buckets = [];

  if (filter === "Daily") {
    for (let i = 6; i >= 0; i -= 1) {
      const date = new Date(now);
      date.setDate(now.getDate() - i);
      const dayStart = new Date(date);
      dayStart.setHours(0, 0, 0, 0);
      const dayEnd = new Date(dayStart.getTime() + DAY_MS - 1);
      buckets.push({
        key: dayStart.toISOString().slice(0, 10),
        label: date.toLocaleDateString(undefined, {
          month: "short",
          day: "numeric",
        }),
        start: dayStart,
        end: dayEnd,
      });
    }
    return buckets;
  }

  if (filter === "Weekly") {
    for (let i = 7; i >= 0; i -= 1) {
      const weekStart = new Date(now);
      const day = weekStart.getDay();
      const diffToMonday = (day + 6) % 7;
      weekStart.setDate(weekStart.getDate() - diffToMonday - i * 7);
      weekStart.setHours(0, 0, 0, 0);

      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 6);
      weekEnd.setHours(23, 59, 59, 999);

      buckets.push({
        key: `${weekStart.getFullYear()}-${weekStart.getMonth()}-${weekStart.getDate()}`,
        label: `${weekStart.toLocaleDateString(undefined, {
          month: "short",
          day: "numeric",
        })}`,
        start: weekStart,
        end: weekEnd,
      });
    }
    return buckets;
  }

  if (filter === "Yearly") {
    for (let i = 4; i >= 0; i -= 1) {
      const year = now.getFullYear() - i;
      const start = new Date(year, 0, 1);
      const end = new Date(year, 11, 31, 23, 59, 59, 999);
      buckets.push({
        key: String(year),
        label: String(year),
        start,
        end,
      });
    }
    return buckets;
  }

  // Monthly (default): last 12 months
  for (let i = 11; i >= 0; i -= 1) {
    const monthDate = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const start = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
    const end = new Date(
      monthDate.getFullYear(),
      monthDate.getMonth() + 1,
      0,
      23,
      59,
      59,
      999,
    );
    buckets.push({
      key: `${monthDate.getFullYear()}-${monthDate.getMonth() + 1}`,
      label: monthDate.toLocaleDateString(undefined, { month: "short" }),
      start,
      end,
    });
  }
  return buckets;
};

const sumByRange = (records, dateKey, valueKey, start, end) =>
  records.reduce((sum, record) => {
    const date = new Date(record[dateKey]);
    if (Number.isNaN(date.getTime()) || date < start || date > end) return sum;
    return sum + (Number(record[valueKey]) || 0);
  }, 0);

const getGrowth = (currentValue, previousValue) => {
  if (!previousValue) {
    if (!currentValue) return { text: "0.0%", positive: true };
    return { text: "+100.0%", positive: currentValue >= 0 };
  }

  const diffPercent = ((currentValue - previousValue) / Math.abs(previousValue)) * 100;
  const positive = diffPercent >= 0;
  return {
    text: `${positive ? "+" : ""}${diffPercent.toFixed(1)}%`,
    positive,
  };
};

/** Net P&L shown for a completed arb trade (matches `/arbitrage/history` normalization). */
const getArbitrageTradeNet = (trade) => {
  if (!trade || trade.status !== "completed") return 0;
  if (typeof trade.realizedNetProfit === "number") {
    return Number(trade.realizedNetProfit);
  }
  if (trade.adminProfit !== null && trade.adminProfit !== undefined) {
    return Number(trade.adminProfit);
  }
  return Number(trade.maxProfit || 0) - Number(trade.estimatedFees || 0);
};

const ProfitSummary = () => {
  const [timeFilter, setTimeFilter] = useState("Monthly");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [incomeSummary, setIncomeSummary] = useState(null);
  const [incomeEntries, setIncomeEntries] = useState([]);
  const [pnlData, setPnlData] = useState({ summary: {}, records: [] });
  const [arbitrageTrades, setArbitrageTrades] = useState([]);
  const [orders, setOrders] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [mlmStats, setMlmStats] = useState({});

  const timeFilters = ["Daily", "Weekly", "Monthly", "Yearly"];

  useEffect(() => {
    const loadDynamicData = async () => {
      try {
        setLoading(true);
        setError("");
        const [
          incomeRes,
          pnlRes,
          ordersRes,
          withdrawalsRes,
          statsRes,
          arbitrageRes,
        ] = await Promise.all([
          userAPI.getIncomeSummary(),
          goldAPI.getPnL(),
          goldAPI.getOrders({ limit: 500 }),
          walletAPI.getWithdrawals({ limit: 200 }),
          mlmAPI.getStats(),
          tradeHistoryAPI.getHistory({ page: 1, limit: 500 }),
        ]);

        setIncomeSummary(incomeRes?.summary || null);
        setIncomeEntries(incomeRes?.recentEntries || []);
        setPnlData({
          summary: pnlRes?.data?.summary || {},
          records: pnlRes?.data?.records || [],
        });
        setArbitrageTrades(arbitrageRes?.trades || []);
        setOrders(Array.isArray(ordersRes?.data) ? ordersRes.data : []);
        setWithdrawals(withdrawalsRes?.data?.withdrawals || []);
        setMlmStats(statsRes || {});
      } catch (err) {
        setError(err?.error || "Failed to load profit summary data");
      } finally {
        setLoading(false);
      }
    };

    loadDynamicData();
  }, []);

  /**
   * Aggregated profit time-series: gold realized PnL + income ledger only.
   * Do not merge arbitrage trades here — arb credited via applyIncomeWithCap is already
   * in the ledger; adding trades too doubled totals (e.g. ~910 vs ~455).
   */
  const combinedProfitRecords = useMemo(() => {
    const goldRecords = (pnlData.records || []).map((record) => ({
      date: record.realizationDate,
      profit: Number(record.netPnL || 0),
    }));
    const incomeRecords = (incomeEntries || []).map((entry) => ({
      date: entry.createdAt,
      profit: Number(entry.netAmount || 0),
    }));
    return [...goldRecords, ...incomeRecords];
  }, [incomeEntries, pnlData.records]);

  const summaryCards = useMemo(() => {
    const now = new Date();

    const todayStart = new Date(now);
    todayStart.setHours(0, 0, 0, 0);
    const todayEnd = new Date(todayStart.getTime() + DAY_MS - 1);
    const yesterdayStart = new Date(todayStart.getTime() - DAY_MS);
    const yesterdayEnd = new Date(todayStart.getTime() - 1);

    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
    const prevMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const prevMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999);

    const ytdStart = new Date(now.getFullYear(), 0, 1);
    const ytdEnd = now;
    const prevYtdStart = new Date(now.getFullYear() - 1, 0, 1);
    const prevYtdEnd = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate(), 23, 59, 59, 999);

    const todayProfit = sumByRange(
      combinedProfitRecords,
      "date",
      "profit",
      todayStart,
      todayEnd,
    );
    const yesterdayProfit = sumByRange(
      combinedProfitRecords,
      "date",
      "profit",
      yesterdayStart,
      yesterdayEnd,
    );
    const monthProfit = sumByRange(
      combinedProfitRecords,
      "date",
      "profit",
      monthStart,
      monthEnd,
    );
    const prevMonthProfit = sumByRange(
      combinedProfitRecords,
      "date",
      "profit",
      prevMonthStart,
      prevMonthEnd,
    );
    const ytdProfit = sumByRange(
      combinedProfitRecords,
      "date",
      "profit",
      ytdStart,
      ytdEnd,
    );
    const prevYtdProfit = sumByRange(
      combinedProfitRecords,
      "date",
      "profit",
      prevYtdStart,
      prevYtdEnd,
    );

    const todayGrowth = getGrowth(todayProfit, yesterdayProfit);
    const monthGrowth = getGrowth(monthProfit, prevMonthProfit);
    const ytdGrowth = getGrowth(ytdProfit, prevYtdProfit);

    return [
      {
        title: "Today's Profit",
        amount: formatCurrency(todayProfit),
        growth: todayGrowth.text,
        positive: todayGrowth.positive,
      },
      {
        title: "Monthly Profit",
        amount: formatCurrency(monthProfit),
        growth: monthGrowth.text,
        positive: monthGrowth.positive,
      },
      {
        title: "YTD Profit",
        amount: formatCurrency(ytdProfit),
        growth: ytdGrowth.text,
        positive: ytdGrowth.positive,
      },
    ];
  }, [combinedProfitRecords]);

  const chartBuckets = useMemo(() => buildBuckets(timeFilter), [timeFilter]);

  const profitTrendData = useMemo(
    () =>
      chartBuckets.map((bucket) => ({
        period: bucket.label,
        profit: sumByRange(
          combinedProfitRecords,
          "date",
          "profit",
          bucket.start,
          bucket.end,
        ),
      })),
    [chartBuckets, combinedProfitRecords],
  );

  const tradingVolumeData = useMemo(
    () =>
      chartBuckets.map((bucket) => ({
        period: bucket.label,
        volume: sumByRange(orders, "createdAt", "totalAmount", bucket.start, bucket.end),
      })),
    [chartBuckets, orders],
  );

  const revenueData = useMemo(() => {
    const bySource = incomeSummary?.bySource || [];
    const totalSourceAmount = bySource.reduce(
      (sum, entry) => sum + (Number(entry.totalNet) || 0),
      0,
    );
    const colors = ["#3B82F6", "#10B981", "#22c55e", "#8B5CF6", "#EF4444", "#14B8A6"];

    if (!bySource.length || totalSourceAmount <= 0) return [];

    return bySource.map((entry, index) => ({
      name: String(entry.source || "Unknown")
        .replaceAll("_", " ")
        .toLowerCase()
        .replace(/\b\w/g, (char) => char.toUpperCase()),
      value: ((Number(entry.totalNet) || 0) / totalSourceAmount) * 100,
      color: colors[index % colors.length],
    }));
  }, [incomeSummary]);

  const completedWithdrawals = useMemo(
    () =>
      withdrawals.filter((item) =>
        ["COMPLETED", "CONFIRMED", "PROCESSED"].includes(
          String(item.status || "").toUpperCase(),
        ),
      ),
    [withdrawals],
  );

  const kpiMetrics = useMemo(() => {
    const completedArbitrageTrades = (arbitrageTrades || []).filter(
      (trade) => trade.status === "completed",
    );
    const totalTrades =
      Number(pnlData.summary?.totalTrades || 0) + completedArbitrageTrades.length;
    const totalArbitragePnL = completedArbitrageTrades.reduce(
      (sum, trade) => sum + getArbitrageTradeNet(trade),
      0,
    );
    const totalPnL = Number(pnlData.summary?.totalPnL || 0) + totalArbitragePnL;
    const avgProfitPerTrade = totalTrades > 0 ? totalPnL / totalTrades : 0;
    const totalWins =
      Number(pnlData.summary?.winningTrades || 0) +
      completedArbitrageTrades.filter((trade) => getArbitrageTradeNet(trade) > 0).length;
    const winRate = totalTrades > 0 ? (totalWins / totalTrades) * 100 : 0;

    return [
      {
        label: "Active Referrals",
        value: formatCount(mlmStats?.activeReferrals || 0),
      },
      {
        label: "Total Trades Executed",
        value: formatCount(totalTrades),
      },
      {
        label: "Average Profit/Trade",
        value: formatCurrency(avgProfitPerTrade),
      },
      {
        label: "Withdrawals Processed",
        value: formatCount(completedWithdrawals.length),
      },
      {
        label: "Win Rate",
        value: `${winRate.toFixed(1)}%`,
      },
    ];
  }, [arbitrageTrades, completedWithdrawals.length, mlmStats, pnlData.summary]);

  return (
    <div className="flex min-h-screen bg-[var(--bg-main)]">
      <Sidebar />
      <div className="flex-1 p-4 pt-16 md:p-8 md:pt-8 md:ml-64">
        {/* Header with Time Filter */}
        <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold mb-2 bg-gradient-to-r from-[#22c55e] to-[#16a34a] bg-clip-text text-transparent">
              Trade History
            </h1>
            <p className="text-[var(--text-primary)] text-sm md:text-base">
              Overview of trading performance, earnings, and history
            </p>
            <p className="text-[var(--text-primary)] text-xs opacity-55 mt-2 max-w-xl">
              Profit cards and trend charts use income ledger + gold only so arbitrage is not
              counted twice (ledger already includes capped arb credits).
            </p>
          </div>

          {/* Time Filter Toggle */}
          <div className="flex gap-2 bg-[var(--bg-card)] p-2 rounded-xl border border-primary/20">
            {timeFilters.map((filter) => (
              <button
                key={filter}
                onClick={() => setTimeFilter(filter)}
                className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                  timeFilter === filter
                    ? "bg-gradient-to-r from-[#22c55e] to-[#16a34a] text-white"
                    : "text-[var(--text-primary)] hover:bg-[#22c55e]/10"
                }`}
              >
                {filter}
              </button>
            ))}
          </div>
        </div>

        {loading ? <p className="text-[var(--text-primary)] text-sm mb-4">Loading dynamic metrics...</p> : null}
        {error ? <p className="text-red-400 text-sm mb-4">{error}</p> : null}

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {summaryCards.map((card, index) => (
            <Card
              key={index}
              className="hover:transform hover:scale-105 transition-all"
            >
              <div className="space-y-4">
                <p className="text-sm text-[var(--text-primary)] opacity-70">{card.title}</p>
                <div className="flex items-baseline gap-2">
                  <h3 className="text-3xl md:text-4xl font-bold text-[var(--text-primary)]">
                    {card.amount}
                  </h3>
                </div>
                <div className="flex items-center gap-2">
                  <span
                    className={`text-sm font-semibold ${
                      card.positive ? "text-green-400" : "text-red-400"
                    }`}
                  >
                    {card.growth}
                  </span>
                  <svg
                    className={`w-4 h-4 ${
                      card.positive
                        ? "text-green-400"
                        : "text-red-400 rotate-180"
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 10l7-7m0 0l7 7m-7-7v18"
                    />
                  </svg>
                  <span className="text-xs text-[var(--text-primary)] opacity-60">
                    vs last period
                  </span>
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* Charts Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Profit Trend Chart - Spans 2 columns */}
          <Card className="lg:col-span-2">
            <h2 className="text-xl md:text-2xl font-bold mb-6 bg-gradient-to-r from-[#22c55e] to-[#16a34a] bg-clip-text text-transparent">
              Profit Trend ({timeFilter})
            </h2>
            <ResponsiveContainer width="100%" height={250} className="md:!h-[300px]">
              <LineChart data={profitTrendData}>
                <defs>
                  <linearGradient
                    id="profitGradient"
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop offset="5%" stopColor="#22c55e" stopOpacity={0.8} />
                    <stop offset="95%" stopColor="#16a34a" stopOpacity={0.1} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#1F2937" />
                <XAxis
                  dataKey="period"
                  stroke="#A1A1AA"
                  style={{ fontSize: "12px" }}
                />
                <YAxis
                  stroke="#A1A1AA"
                  style={{ fontSize: "12px" }}
                  tickFormatter={(value) => formatCurrency(value)}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "#161A1D",
                    border: "1px solid #22c55e",
                    borderRadius: "12px",
                    color: "#22c55e",
                  }}
                  formatter={(value) => [
                    formatCurrency(value),
                    "Profit",
                  ]}
                />
                <Legend wrapperStyle={{ color: "#22c55e" }} iconType="circle" />
                <Line
                  type="monotone"
                  dataKey="profit"
                  stroke="#22c55e"
                  strokeWidth={3}
                  fill="url(#profitGradient)"
                  name="Profit"
                  dot={{ fill: "#22c55e", r: 4 }}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </Card>

          {/* KPI Metrics */}
          <Card>
            <h2 className="text-xl md:text-2xl font-bold mb-6 bg-gradient-to-r from-[#22c55e] to-[#16a34a] bg-clip-text text-transparent">
              Key Performance Indicators
            </h2>
            <div className="space-y-6">
              {kpiMetrics.map((metric, index) => (
                <div
                  key={index}
                  className="pb-4 border-b border-primary/10 last:border-0"
                >
                  <p className="text-sm text-[var(--text-primary)] opacity-60 mb-2">
                    {metric.label}
                  </p>
                  <p className="text-2xl font-bold text-[var(--text-primary)]">{metric.value}</p>
                </div>
              ))}
            </div>
          </Card>
        </div>

        {/* Second Row Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Revenue Breakdown */}
          <Card>
            <h2 className="text-xl md:text-2xl font-bold mb-6 bg-gradient-to-r from-[#22c55e] to-[#16a34a] bg-clip-text text-transparent">
              Revenue Breakdown
            </h2>
            {revenueData.length === 0 ? (
              <div className="h-[250px] md:h-[300px] flex items-center justify-center">
                <p className="text-[var(--text-primary)] opacity-60">No income source data available yet.</p>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={250} className="md:!h-[300px]">
                <PieChart>
                  <Pie
                    data={revenueData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {revenueData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#161A1D",
                      border: "1px solid #22c55e",
                      borderRadius: "12px",
                      color: "#22c55e",
                    }}
                    formatter={(value) => [`${Number(value).toFixed(1)}%`, "Share"]}
                  />
                  <Legend wrapperStyle={{ color: "#22c55e" }} iconType="circle" />
                </PieChart>
              </ResponsiveContainer>
            )}
          </Card>

          {/* Trading Volume */}
          <Card>
            <h2 className="text-xl md:text-2xl font-bold mb-6 bg-gradient-to-r from-[#22c55e] to-[#16a34a] bg-clip-text text-transparent">
              Trading Volume ({timeFilter})
            </h2>
            <ResponsiveContainer width="100%" height={250} className="md:!h-[300px]">
              <BarChart data={tradingVolumeData}>
                <defs>
                  <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#22c55e" stopOpacity={1} />
                    <stop offset="100%" stopColor="#16a34a" stopOpacity={0.8} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#1F2937" />
                <XAxis
                  dataKey="period"
                  stroke="#A1A1AA"
                  style={{ fontSize: "12px" }}
                />
                <YAxis
                  stroke="#A1A1AA"
                  style={{ fontSize: "12px" }}
                  tickFormatter={(value) => formatCurrency(value)}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "#161A1D",
                    border: "1px solid #22c55e",
                    borderRadius: "12px",
                    color: "#22c55e",
                  }}
                  formatter={(value) => [
                    formatCurrency(value),
                    "Volume",
                  ]}
                />
                <Bar
                  dataKey="volume"
                  fill="url(#barGradient)"
                  radius={[8, 8, 0, 0]}
                  name="Volume"
                />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </div>

        {/* Footer */}
        <div className="text-center py-6">
          <p className="text-sm text-[var(--text-primary)] opacity-60">
            © 2026 Aiarbix. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ProfitSummary;
